import { z } from 'zod';

import {
	PHONE_PREFIX_RULES,
	type ExtendedPhonePrefixEnumType,
} from '../phonePrefixes';

import { checkIsPhonePrefix } from './checkIsPhonePrefix';

export type PhoneSchemaParams = {
	getPhoneLengthError: (params: { min: number; max: number }) => string;
	required?: boolean;
};

const createBasePhoneSchema = ({
	getPhoneLengthError,
	required = false,
}: PhoneSchemaParams) => {
	const baseSchema = z
		.object({
			prefix: z.custom<ExtendedPhonePrefixEnumType>(checkIsPhonePrefix),
			value: z.string(),
		})
		.superRefine(({ prefix, value }, ctx) => {
			const maybeRules = PHONE_PREFIX_RULES.get(prefix);

			const isOptionalEmptyValue = !required && !value;
			const shouldSkipPhoneLengthValidation =
				!maybeRules || isOptionalEmptyValue;

			if (shouldSkipPhoneLengthValidation) return undefined;

			const valueLength = value.length;
			const isWithinAllowedLength =
				valueLength >= maybeRules.min && valueLength <= maybeRules.max;

			if (isWithinAllowedLength) return undefined;

			ctx.addIssue({
				code: 'custom',
				message: getPhoneLengthError(maybeRules),
				path: ['value'],
			});
		});

	return baseSchema;
};

type BasePhoneSchema = ReturnType<typeof createBasePhoneSchema>;
type OptionalPhoneSchema = z.ZodOptional<z.ZodNullable<BasePhoneSchema>>;

export function createPhoneSchema(
	params: PhoneSchemaParams & { required: true },
): BasePhoneSchema;
export function createPhoneSchema(
	params: PhoneSchemaParams,
): OptionalPhoneSchema;
export function createPhoneSchema(
	params: PhoneSchemaParams,
): BasePhoneSchema | OptionalPhoneSchema {
	const baseSchema = createBasePhoneSchema(params);

	if (params.required) {
		return baseSchema;
	}

	return baseSchema.nullable().optional();
}
